import React, {useState, useRef, ChangeEvent} from 'react';
import './ArrPaginationPro.scss';
import {
  Divider,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrTextField from '../arr-text-field/ArrTextField';
import ArrIconButton from '../arr-icon-button/ArrIconButton';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';

export interface ArrPaginationProProps {
  currentPage: number;
  pageSize: number;
  totalEntries: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;

  availablePageSizes?: number[];
  className?: string;
}

const ArrPaginationPro: React.FC<ArrPaginationProProps> = ({
  currentPage,
  pageSize,
  totalEntries,
  onPageChange,
  onPageSizeChange,

  availablePageSizes = [10, 25, 50, 100, 200, 500],
  className = '',
}) => {
  const perPageAnchorRef = useRef<HTMLDivElement | null>(null);

  const calculatedStartItem = (currentPage - 1) * pageSize + 1;
  const [selectOpen, setSelectOpen] = useState(false);
  const [indexInput, setIndexInput] = useState<string>(
    calculatedStartItem.toString()
  );

  const currentIndex = parseInt(indexInput) || calculatedStartItem;
  const startItem = currentIndex;
  const endItem = Math.min(currentIndex + pageSize - 1, totalEntries);

  const handleIndexInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/^\d*$/.test(value)) {
      setIndexInput(value);
    }
  };

  const handleIndexInputBlur = () => {
    const indexNumber = parseInt(indexInput);
    if (indexNumber >= 1 && indexNumber <= totalEntries) {
      onPageChange(indexNumber);

      setIndexInput(indexNumber.toString());
    } else {
      setIndexInput(startItem.toString());
    }
  };

  const goToFirstPage = () => {
    onPageChange(1);
    setIndexInput('1');
  };

  const goToPreviousPage = () => {
    const currentIndex = parseInt(indexInput) || startItem;
    const newStartIndex = Math.max(currentIndex - pageSize, 1);
    onPageChange(newStartIndex);
    setIndexInput(newStartIndex.toString());
  };

  const goToNextPage = () => {
    const currentIndex = parseInt(indexInput) || startItem;
    const newStartIndex = Math.min(currentIndex + pageSize, totalEntries);
    onPageChange(newStartIndex);
    setIndexInput(newStartIndex.toString());
  };

  const goToLastPage = () => {
    const lastPageStartIndex = totalEntries - (totalEntries % pageSize) + 1;
    const actualLastStartIndex =
      totalEntries % pageSize === 0
        ? totalEntries - pageSize + 1
        : lastPageStartIndex;
    onPageChange(actualLastStartIndex);
    setIndexInput(actualLastStartIndex.toString());
  };

  const handlePageSizeChange = (e: SelectChangeEvent<number>) => {
    const newPageSize = Number(e.target.value);
    onPageSizeChange(newPageSize);

    const currentIndex = parseInt(indexInput) || startItem;
    onPageChange(currentIndex);
  };

  return (
    <div
      className={`arr-pagination-pro ${className}`}
      data-testid={`arr-pagination-pro-showing-to-of-entries`}
    >
      <Typography
        className="pagination-info typo-text body3_size"
        data-testid={`arr-pagination-pro-showing-to-of-entries-text`}
      >
        Showing {startItem} to {endItem} of {totalEntries} entries
      </Typography>
      <div className="per-page-section" data-testid={`arr-pagination-pro-div`}>
        <div
          className="per-page-select-wrapper"
          ref={perPageAnchorRef}
          data-testid={`arr-pagination-pro-dropdown-wrapper`}
        >
          <Select
            className="per-page-select"
            open={selectOpen}
            onOpen={() => setSelectOpen(true)}
            onClose={() => setSelectOpen(false)}
            value={pageSize}
            onChange={handlePageSizeChange}
            size="small"
            variant="outlined"
            SelectDisplayProps={{'data-testid': `per-page-control`}}
            IconComponent={() => null}
            renderValue={(value) => (
              <div
                className="select-render-value"
                data-testid={`arr-pagination-pro-per-page-wrapper`}
              >
                <Typography
                  className="items-per-page typo-text buttonSmall_size"
                  data-testid={`arr-pagination-pro-per-page-text`}
                >
                  Per page:{' '}
                </Typography>
                <span
                  className="select-label"
                  data-testid={`arr-pagination-pro-select-icon`}
                >
                  {value}
                </span>
                {selectOpen ? (
                  <ArrowDropUpIcon className="select-caret" />
                ) : (
                  <ArrowDropDownIcon className="select-caret" />
                )}
              </div>
            )}
            MenuProps={{
              anchorEl: perPageAnchorRef.current,
              anchorOrigin: {vertical: 'bottom', horizontal: 'left'},
              transformOrigin: {vertical: 'top', horizontal: 'left'},
              PaperProps: {
                className: 'per-page-dropdown custom-dropdown-menu',
              },
            }}
          >
            {availablePageSizes.map((opt) => (
              <MenuItem
                className="custom-menu-item"
                key={opt}
                value={opt}
                data-testid={`per-page-${opt}`}
              >
                {opt}
              </MenuItem>
            ))}
          </Select>
        </div>
      </div>
      <Divider
        orientation="vertical"
        variant="middle"
        flexItem
        className="divider"
      />
      <div
        className="pagination-controls"
        role="group"
        aria-label="Pagination controls"
        data-testid={`arr-pagination-pro-div-nav-buttons-wrapper`}
      >
        <ArrIconButton
          onClick={goToFirstPage}
          disabled={currentIndex === 1}
          className="pagination-button"
          ariaLabel="First Page"
          size="medium"
          icon={<FirstPageIcon />}
          testId="arr-pagination-pro-first-page-icon-button"
        />

        <ArrIconButton
          onClick={goToPreviousPage}
          disabled={currentPage === 1}
          className="pagination-button"
          ariaLabel="Previous"
          size="medium"
          icon={<NavigateBeforeIcon />}
          testId="arr-pagination-pro-previous-icon-button"
        />
        <ArrTextField
          className="page-input"
          value={indexInput}
          onChange={handleIndexInputChange}
          onBlur={handleIndexInputBlur}
          title="Current index"
          aria-label="Current index number"
          aria-describedby="index-info"
          sizeVariant="small"
          state="enabled"
          dataTestId="pagination-input"
        />

        <ArrIconButton
          onClick={goToNextPage}
          disabled={endItem >= totalEntries}
          className="pagination-button"
          ariaLabel="Next"
          size="medium"
          icon={<NavigateNextIcon />}
          testId="arr-pagination-pro-next-icon-button"
        />
        <ArrIconButton
          onClick={goToLastPage}
          disabled={endItem >= totalEntries}
          className="pagination-button"
          ariaLabel="Last Page"
          size="medium"
          icon={<LastPageIcon />}
          testId="arr-pagination-pro-last-page-icon-button"
        />
      </div>
    </div>
  );
};

export default ArrPaginationPro;
